/* eslint-disable react-hooks/exhaustive-deps */
"use client"

import { useState, useEffect, useMemo } from "react"
import { Box, Grid, Paper, Typography, CircularProgress } from "@mui/material"
import { useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "../../store"
import { requestSignalProfitByCompanyAndProject, requestSignalProfitCoins, requestSignalProfitCompanies, requestSignalProfitPC, requestSignalProfitPeriods, requestSignalProfitProjectsByCompany } from "../../services/signalApi"
import { setLoadingCoins, setLoadingCompanies, setLoadingPC, setLoadingPeriods, setLoadingProfit, setLoadingProjects } from "../../store/slices/profit"
import { refreshToken } from "../../store/slices/auth"
import { FiltersSection } from "../../components/filters-section"
import { BoardProcessMenu } from "../../components/BoardProcessMenu"
import { BOARD_TYPE_PROFIT } from "../../constants/boardProcessConsts"
import DateSelectorButton from "../../components/DateSelectorButton"
import { GenericBarChart } from "../../components/GenericBarChart"


export function ProfitDashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams()

  const authState = useSelector((state: RootState) => state.auth);
  const profitState = useSelector((state: RootState) => state.profit);
  
  const [error] = useState<string | null>(null)
  
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([])
  
  
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    company: 'all',
    scale: '0',
    project: 'all',
    pc: '',
    resource: 'all',
    coin: '',
  })
  
  const email = searchParams.get('email') || ''

  const hasMainData = profitState.coins.length > 0 && profitState.companies.length > 0 && !!filterValues.coin;

  // refresh token
  useEffect(() => {
    if (authState.token && authState.moduleId && authState.userId && !authState.connectionId) {
      dispatch(refreshToken(authState.token));
    }
  }, [authState.token, authState.moduleId, authState.userId]);

  // get coins
  useEffect(() => {
    if (!authState.connectionId || !email || profitState.loadingCoins || profitState.coins.length) {
      return
    }
    dispatch(setLoadingCoins(true));
    requestSignalProfitCoins();
  }, [authState.connectionId, email]);

  // set default coin
  useEffect(() => {
    if (profitState.loadingCoins || profitState.coins.length === 0) {
      return
    }
    setFilterValues((prev) => ({
      ...prev,
      coin: profitState.coins[0].Simbolo,
    }))
  }, [profitState.coins.length]);

  // get companies
  useEffect(() => {
    if (!authState.connectionId || !email || profitState.loadingCompanies || profitState.companies.length) {
      return
    }
    dispatch(setLoadingCompanies(true));
    requestSignalProfitCompanies(email);
  }, [authState.connectionId, email]);

  // get periods data
  useEffect(() => {
    if (!hasMainData ||profitState.loadingPeriods || profitState.loadingCompanies || !profitState.companies.length) {
      return
    }
    setSelectedPeriods([])
    dispatch(setLoadingPeriods(true));
    requestSignalProfitPeriods(
      filterValues.company === 'all' ? '' : filterValues.company, 
      filterValues.scale
    );
  }, [hasMainData, filterValues.company, filterValues.scale]);

  // get projects
  useEffect(() => {
    if (!hasMainData || profitState.loadingCompanies || profitState.loadingProjects || profitState.loadingPeriods || !profitState.periods.length) {
      return
    }

    dispatch(setLoadingProjects(true));
    setFilterValues((prev) => ({
      ...prev,
      project: 'all'
    }))
    requestSignalProfitProjectsByCompany(filterValues.company === 'all' ? '' : filterValues.company);
  }, [profitState.loadingPeriods]);

  // get PC
  useEffect(() => {
    if (!hasMainData || profitState.loadingPC || profitState.loadingProjects) {
      return
    }
    dispatch(setLoadingPC(true));
    setFilterValues((prev) => ({
      ...prev,
      pc: ''
    }))
    if (filterValues.project === 'all') {
      console.log('dispatch(setLoadingPC(false));')
      dispatch(setLoadingPC(false));
      getProfit()
      return;
    }
    requestSignalProfitPC(filterValues.project === 'all' ? '' : filterValues.project);
  }, [profitState.loadingProjects, filterValues.project]);

  const getProfit = async () => {
    dispatch(setLoadingProfit(true));
    requestSignalProfitByCompanyAndProject(
      filterValues.company === 'all' ? '' : filterValues.company,
      filterValues.project === 'all' ? '' : filterValues.project,
      filterValues.scale,
      selectedPeriods,
      filterValues.pc,
    );
    
  }

  // get profit data
  useEffect(() => {

    if (
      !hasMainData 
      || profitState.loadingPeriods 
      || profitState.loadingProjects 
      || profitState.loadingProfit 
      || profitState.loadingPC 
      || (filterValues.project && !profitState.pc.length)
    ) {
      return
    }
    getProfit()
  }, [filterValues.scale, profitState.loadingPC, filterValues.pc, selectedPeriods]);
  

  const handleFilterChange = (filterId: string, value: string) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterId]: value,
    }))
  }

  const alternativeCoinSimbol = !hasMainData || filterValues.coin === profitState.coins[0].Simbolo
      ? 'PEN'
      : 'USD'

  const getDataSetsData = (labelPrefix: 'Lucro' | 'Margen', sufix: '' | '_A') => {
    if (!profitState.profit || profitState.profit.length === 0) {
      return { labels: [], datasets: [] }
    }

    const sortedData = [...profitState.profit].sort((a, b) => a.Periodo.localeCompare(b.Periodo));
    const labels = sortedData.map(item => item.Periodo);
    const coin = alternativeCoinSimbol;
    
    const realData = sortedData.map((item: any) => 
      // coin === 'USD' ? item.MargenRealUSD : item.MargenRealPEN
      item[`${labelPrefix}Real${coin}${sufix}`]
    );
    const metaData = sortedData.map((item: any) => 
      // coin === 'USD' ? item.MargenMetaUSD : item.MargenMetaPEN
      item[`${labelPrefix}Meta${coin}${sufix}`]
    );
    const baseData = sortedData.map((item: any) => 
      // coin === 'USD' ? item.MargenBaseUSD : item.MargenBasePEN
      item[`${labelPrefix}Base${coin}${sufix}`]
    );

    const datasets = [
      {
        label: 'Real',
        data: realData,
        backgroundColor: 'rgba(26, 188, 156, 0.7)',
        borderColor: '#1abc9c',
      },
      {
        label: 'Meta',
        data: metaData,
        backgroundColor: 'rgba(241, 196, 15, 0.7)',
        borderColor: '#f1c40f',
      },
      {
        label: 'Base',
        data: baseData,
        backgroundColor: 'rgba(46, 134, 193, 0.7)',
        borderColor: '#2e86c1',
      },
    ];

    return { labels, datasets };
  }

  

  const { labels: profitLabels, datasets: profitDatasets } = useMemo(() => getDataSetsData('Lucro', ''), [profitState.profit, alternativeCoinSimbol]);

  const { labels: marginLabels, datasets: marginDatasets } = useMemo(() => getDataSetsData('Margen', ''), [profitState.profit, alternativeCoinSimbol]);

  const { labels: profitLabelsA, datasets: profitDatasetsA } = useMemo(() => getDataSetsData('Lucro', '_A'), [profitState.profit, alternativeCoinSimbol]);

  const { labels: marginLabelsA, datasets: marginDatasetsA } = useMemo(() => getDataSetsData('Margen', '_A'), [profitState.profit, alternativeCoinSimbol]);

  if (profitState.loadingCompanies) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  return (
    <Grid container sx={{ width: "100%", height: "100%", display: "flex", minHeight: '90vh' }}>
      <Grid item xs={12}>
        <FiltersSection
          filters={[
            {
              label: "Empresa",
              value: "company",
              options: [
                { label: "Todas", value: "all" },
                ...profitState.companies.map((c) => ({ label: c.Descripcion, value: c.CodEmpresa }))
              ],
            },
            {
              label: "Escala",
              value: "scale",
              options: [
                { label: "Semanal", value: "1" },
                { label: "Mensual", value: "0" },
                { label: "Anual", value: "2" },
              ],
            },
            {
              label: "Periodo",
              value: "period",
              type: "custom",
              component: (
                <DateSelectorButton
                  multiple={true}
                  dates={profitState.periods.map(p => ({...p, DesPeriodo: p.Descripcion}))}
                  // selectedDate={filterValues.period}
                  onDatesChange={(codigos: string[]) => {
                    setSelectedPeriods(codigos)
                    // handleFilterChange('period', codigos.join(','))
                  }}
                  selectedDates={selectedPeriods}
                  // onDateChange={(codigo: string) => handleFilterChange('period', codigo)}
                  groupBy={filterValues.scale === '2' ? undefined : "Anio"}
                  buttonColor="secondary"
                  modalTitle="Seleccionar período"
                  placeholder="Elige una fecha"
                />
              )
            },
            {
              label: "Proyecto",
              value: "project",
              options: [
                { label: "Todas", value: "all" },
                ...profitState.projects.map((p) => ({ label: p.Descripcion, value: p.CodProyecto }))
              ],
            },
            {
              label: "Partida de Control",
              value: "pc",
              type: "search",
              width: 300,
              options: profitState.pc.map((p) => ({ label: p.Descripcion, value: p.CodPC })),
            },
            {
              label: "Moneda",
              value: "coin",
              options: profitState.coins.map((p) => ({ label: p.Descripcion, value: p.Simbolo })),
            },
          ]} 
          onFilterChange={handleFilterChange} 
          values={filterValues} 
          actions={
            <BoardProcessMenu
              boardType={BOARD_TYPE_PROFIT}
              codProyecto={filterValues.project === 'all' ? '' : filterValues.project}
            />
          }
          // onSearch={() => {}}
        />
      </Grid>
      <Grid container spacing={2} sx={{ minHeight: '45%', maxHeight: '45vh' }} style={{ marginTop: '16px' }}>
        <Grid item sx={{ width: `50%` }}>
          <Paper sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
            <GenericBarChart
              canShowAsGrid={true}
              title={`Lucro Mensual (${filterValues.coin})`}
              labels={profitLabels}
              datasets={profitDatasets}
              height={330}
              options={{
                maintainAspectRatio: false,
                scales: {
                  y: { beginAtZero: true, title: { display: true, text: `Monto (${filterValues.coin})` } },
                  x: { grid: { display: false } }
                },
                onClick: () => {}
              }}
            />
          </Paper>
        </Grid>

        <Grid item sx={{ width: `50%` }}>
          <Paper sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
            <GenericBarChart
              canShowAsGrid={true}
              title={`Lucro Acumulado (${filterValues.coin})`}
              labels={profitLabelsA}
              datasets={profitDatasetsA}
              height={330}
              options={{
                maintainAspectRatio: false,
                scales: {
                  y: { beginAtZero: true, title: { display: true, text: `Monto (${filterValues.coin})` } },
                  x: { grid: { display: false } }
                },
                onClick: () => {}
              }}
            />
          </Paper>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ minHeight: '45%', maxHeight: '45vh' }} style={{ marginTop: '16px' }}>
        <Grid item sx={{ width: `50%` }}>
          <Paper sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
            <GenericBarChart
              canShowAsGrid={true}
              title={`Margen Mensual (${filterValues.coin === 'USD' ? 'USD' : 'PEN'})`}
              labels={marginLabels}
              datasets={marginDatasets}
              height={330}
              options={{
                maintainAspectRatio: false,
                scales: {
                  y: { beginAtZero: true, title: { display: true, text: `Margen (%)` } },
                  x: { grid: { display: false } }
                },
                onClick: () => {}
              }}
            />
          </Paper>
        </Grid>

        <Grid item sx={{ width: `50%` }}>
          <Paper sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
            <GenericBarChart
              canShowAsGrid={true}
              title={`Margen Acumulado (${filterValues.coin === 'USD' ? 'USD' : 'PEN'})`}
              labels={marginLabelsA}
              datasets={marginDatasetsA}
              height={330}
              options={{
                maintainAspectRatio: false,
                scales: {
                  y: { beginAtZero: true, title: { display: true, text: `Margen (%)` } },
                  x: { grid: { display: false } }
                },
                onClick: () => {}
              }}
            />
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  )
}